import React, { useEffect, useState } from "react";
import { FaStar } from "react-icons/fa";
import { sendRequest } from "../../utils/api";
import FullPageSpinner from "../../components/FullPageSpinner";
import { useToast } from "../../context/ToastContext";
import Review from "../../models/Reviews";
import { SVGProps } from "react";

const StarIcon = FaStar as React.FC<SVGProps<SVGSVGElement>>;


type ReviewCardProps = {
  review: Review;
  onDelete: (id: string) => void;
  onResolve: (id: string) => void;
};

/* ================= REVIEW CARD ================= */
const ReviewCard: React.FC<ReviewCardProps> = ({
  review,
  onDelete,
  onResolve,
}) => {
  const { id, room, customer } = review;

  return (
    <div style={styles.card}>
      {/* Header */}
      <div style={styles.header}>
        <h4>Room {room.number}</h4>

        <span
          style={{
            ...styles.badge,
            background: review.isResolved ? "#e6f7ee" : "#fdecea",
            color: review.isResolved ? "#1a7f37" : "#d93025",
          }}
        >
          {review.isResolved ? "RESOLVED" : "NOT RESOLVED"}
        </span>
      </div>

      {/* Guest + Rating */}
      <div style={styles.subHeader}>
        <span>
          <strong>Guest:</strong> {customer.name}
        </span>

   <div style={styles.rating}>
  {[...Array(5)].map((_, i) => (
    <StarIcon
      key={i}
      color={i < review.rating ? "#f5a623" : "#ddd"}
      width={15}
      height={15}
    />
  ))}
</div>
      </div>

      {/* Comment */}
      <p style={styles.comment}>“{review.review}”</p>

      {/* Actions */}
      <div style={styles.actions}>
        <button style={styles.deleteBtn} onClick={() => onDelete(id)}>
          Delete
        </button>

        <button
          style={review.isResolved ? styles.resolvedBtn : styles.resolveBtn}
          onClick={() => onResolve(review.id)}
          disabled={review.isResolved}
        >
          {review.isResolved ? "✔ Resolved" : "Mark as Resolved"}
        </button>
      </div>
    </div>
  );
};

/* ================= PAGE ================= */
const ReviewsPage: React.FC = () => {
  const { showToast } = useToast();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(false);

  const [filter, setFilter] = useState<"all" | "resolved" | "unresolved">("all");
  const [currentPage, setCurrentPage] = useState(1);
  const REVIEWS_PER_PAGE = 5;

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {

        const data = await sendRequest("GET", "/guests/reviews");
        setReviews(data);
      } catch (err: any) {
        showToast("Failed to load data", { type: "error" });
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);


  const deleteReview = async (id: string) => {
    try {
      setLoading(true);
      sendRequest("DELETE", `/guests/reviews/${id}/delete`);
      showToast("Review Deleted Successfully", { type: "success" });
      setReviews((prev) => prev.filter((r) => r.id !== id));

    } catch (err) {
      showToast(
        "An error occurred while deleting guest review. "
        + "Please try again later.", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const resolveReview = async (id: string) => {
    try {
      setLoading(true);
      sendRequest("PUT", `/guests/reviews/${id}/update`);
      showToast("Review Resolved Successfully", { type: "success" });
      setReviews((prev) =>
        prev.map((r) =>
          r.id === id ? { ...r, isResolved: true } : r
        )
      );

    } catch (err) {
      showToast(
        "An error occurred while resolved guest review. "
        + "Please try again later.", { type: "error" });
    } finally {
      setLoading(false);
    }
  };


  const filteredReviews = reviews.filter((r) => {
    if (filter === "resolved") return r.isResolved;
    if (filter === "unresolved") return !r.isResolved;
    return true;
  });

  const totalPages = Math.ceil(filteredReviews.length / REVIEWS_PER_PAGE);

  const paginatedReviews = filteredReviews.slice(
    (currentPage - 1) * REVIEWS_PER_PAGE,
    currentPage * REVIEWS_PER_PAGE
  );


  const getEmptyState = () => {
    if (reviews.length === 0) {
      return {
        title: "No Reviews Yet",
        text: "Guests haven’t left any feedback yet. Once they do, reviews will appear here.",
        hint: "Encourage guests to leave reviews by sharing your guest portal link.",
      };
    }

    if (filter === "resolved") {
      return {
        title: "No Resolved Reviews",
        text: "You haven’t resolved any reviews yet.",
        hint: "Resolve guest complaints to keep track of completed issues.",
      };
    }

    if (filter === "unresolved") {
      return {
        title: "No Pending Reviews 🎉",
        text: "Great job! There are no unresolved reviews.",
        hint: "All guest issues have been handled.",
      };
    }

    return {
      title: "No Reviews Found",
      text: "",
      hint: "",
    };
  };

  const emptyState = getEmptyState();

  return (
    <div style={styles.container}>
      <h1 className="text-3xl font-bold text-gray-800 mb-5">Reviews</h1>
      <div style={styles.filterContainer}>
        {["all", "resolved", "unresolved"].map((type) => (
          <button
            key={type}
            onClick={() => {
              setFilter(type as any);
              setCurrentPage(1); // reset page
            }}
            style={{
              ...styles.filterBtn,
              ...(filter === type ? styles.activeFilterBtn : {}),
            }}
          >
            {type === "all"
              ? "All"
              : type === "resolved"
                ? "Resolved"
                : "Unresolved"}
          </button>
        ))}
      </div>

      <FullPageSpinner show={loading} />

      {!loading && filteredReviews.length === 0 ? (
        <div style={styles.emptyContainer}>
          <div style={styles.emptyCard}>
            {FaStar({ size: 40, color: "#ccc" })}
            <h2 style={styles.emptyTitle}>{emptyState.title}</h2>

            <p style={styles.emptyText}>{emptyState.text}</p>

            <p style={styles.emptyHint}>{emptyState.hint}</p>
          </div>
        </div>
      ) : (
        paginatedReviews.map((review) => (
          <ReviewCard
            key={review.id}
            review={review}
            onDelete={deleteReview}
            onResolve={resolveReview}
          />
        ))
      )}

      {totalPages > 1 && (
        <div style={styles.pagination}>
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
          >
            Prev
          </button>

          <span>
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ReviewsPage;

/* ================= STYLES ================= */
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    // maxWidth: "600px",
    margin: "0 auto",
    padding: "20px",
  },
  card: {
    border: "1px solid #eee",
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "15px",
    background: "#fff",
    boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "8px",
  },
  badge: {
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "600",
  },
  subHeader: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "10px",
  },
  rating: {
    display: "flex",
    gap: "3px",
  },
  comment: {
    fontStyle: "italic",
    marginBottom: "12px",
    color: "#444",
  },
  actions: {
    display: "flex",
    gap: "10px",
  },
  deleteBtn: {
    background: "#fff",
    border: "1px solid #d93025",
    color: "#d93025",
    padding: "6px 12px",
    borderRadius: "6px",
    cursor: "pointer",
  },
  resolveBtn: {
    background: "#007bff",
    border: "none",
    color: "#fff",
    padding: "6px 12px",
    borderRadius: "6px",
    cursor: "pointer",
  },
  resolvedBtn: {
    background: "#1a7f37",
    border: "none",
    color: "#fff",
    padding: "6px 12px",
    borderRadius: "6px",
    cursor: "not-allowed",
  },
  emptyContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "60vh",
  },

  emptyCard: {
    textAlign: "center",
    padding: "40px",
    borderRadius: "16px",
    background: "#fff",
    boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
    border: "1px solid #eee",
    maxWidth: "400px",
  },

  emptyTitle: {
    marginTop: "15px",
    fontSize: "20px",
    fontWeight: "600",
    color: "#333",
  },

  emptyText: {
    marginTop: "8px",
    fontSize: "14px",
    color: "#777",
  },
  emptyHint: {
    marginTop: "12px",
    fontSize: "13px",
    color: "#999",
    fontStyle: "italic",
  },


  filterContainer: {
    display: "flex",
    gap: "10px",
    marginBottom: "20px",
  },

  filterBtn: {
    padding: "6px 14px",
    borderRadius: "20px",
    border: "1px solid #ddd",
    background: "#fff",
    cursor: "pointer",
    fontSize: "13px",
  },

  activeFilterBtn: {
    background: "#007bff",
    color: "#fff",
    border: "1px solid #007bff",
  },

  pagination: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "15px",
    marginTop: "20px",
  },
};